/**
 * Uploads — images to shx.gg, files to userdrive, metadata stripped first.
 *
 * Privacy contract (CLAUDE.md §1 spirit): nothing leaves the browser with
 * EXIF/GPS/camera/app metadata attached. Guaranteed for images (canvas
 * re-encode drops ALL metadata chunks; GIF gets comment/plain-text extension
 * blocks spliced out byte-wise). Other file types: there is no generic
 * metadata stripper — the UI must say so rather than pretend.
 *
 * Uploads go to third-party clearnet hosts: the host sees the uploader's IP
 * and the content is public-by-link. That is the user's explicit choice here.
 */

export const USERDRIVE_URL = 'https://dns700.userdrive.org/cgi-bin/upload.cgi?upload_type=file&utype=anon';
export const USERDRIVE_LINK = (code: string) => `https://dns700.userdrive.org/d/${code}`;
export const SHX_URL = 'https://shx.gg/api/files/create';

export const MAX_IMAGE_BYTES = 10 * 1024 * 1024;
export const MAX_FILE_BYTES = 50 * 1024 * 1024;

// --- metadata stripping -----------------------------------------------------

/** Re-encode via canvas: drops every metadata chunk (EXIF, GPS, ICC, XMP…). */
async function stripViaCanvas(file: File): Promise<Blob> {
  const bmp = await createImageBitmap(file);
  const canvas = document.createElement('canvas');
  canvas.width = bmp.width;
  canvas.height = bmp.height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  ctx.drawImage(bmp, 0, 0);
  bmp.close();
  const type = file.type === 'image/png' ? 'image/png'
    : file.type === 'image/webp' ? 'image/webp'
    : 'image/jpeg'; // jpeg default: re-encode q0.92, kills EXIF
  const blob = await new Promise<Blob | null>(res => canvas.toBlob(res, type, 0.92));
  if (!blob) throw new Error('Image re-encode failed');
  return blob;
}

/**
 * GIFs: splicing out Comment (0x21 0xFE) and Plain Text (0x21 0x01) extension
 * blocks byte-wise. Application extensions (0xFF, e.g. NETSCAPE looping) are
 * kept so animation survives. Pure function — unit-tested.
 */
export function stripGifMetadata(bytes: Uint8Array): Uint8Array {
  if (bytes.length < 6 || bytes[0] !== 0x47 || bytes[1] !== 0x49 || bytes[2] !== 0x46) {
    return bytes; // not a GIF, untouched
  }
  const out: number[] = [];
  let i = 0;
  // header + LSD + global color table
  const gctSize = (bytes[10] & 0x80) ? 3 * (1 << ((bytes[10] & 0x07) + 1)) : 0;
  const headerEnd = 13 + gctSize;
  for (let j = 0; j < headerEnd && j < bytes.length; j++) out.push(bytes[j]);
  i = headerEnd;

  const skipSubBlocks = (idx: number): number => {
    let k = idx;
    while (k < bytes.length) {
      const sz = bytes[k];
      k += 1 + sz;
      if (sz === 0) break;
    }
    return k;
  };

  while (i < bytes.length) {
    const b = bytes[i];
    if (b === 0x3b) { out.push(b); i++; break; } // trailer
    if (b === 0x21) { // extension
      const label = bytes[i + 1];
      if (label === 0xfe || label === 0x01) { // comment / plain text — DROP
        i = skipSubBlocks(i + 2);
        continue;
      }
      // keep other extensions verbatim (block header + sub-blocks)
      const end = skipSubBlocks(i + 2 + (label === 0xf9 ? 4 : label === 0xff ? 11 : 2));
      for (let j = i; j < end && j < bytes.length; j++) out.push(bytes[j]);
      i = end;
      continue;
    }
    if (b === 0x2c) { // image descriptor + optional LCT + LZW data
      const lct = (bytes[i + 9] & 0x80) ? 3 * (1 << ((bytes[i + 9] & 0x07) + 1)) : 0;
      const dataStart = i + 10 + lct + 1; // +1 = LZW min code size byte
      const end = skipSubBlocks(dataStart);
      for (let j = i; j < end && j < bytes.length; j++) out.push(bytes[j]);
      i = end;
      continue;
    }
    out.push(b);
    i++;
  }
  return new Uint8Array(out);
}

export async function stripImageMetadata(file: File): Promise<Blob> {
  if (file.type === 'image/gif') {
    const stripped = stripGifMetadata(new Uint8Array(await file.arrayBuffer()));
    return new Blob([stripped.buffer as ArrayBuffer], { type: 'image/gif' });
  }
  return stripViaCanvas(file);
}

// --- uploads ------------------------------------------------------------------

function extractUrl(json: unknown, raw: string): string {
  const j = json as Record<string, unknown> | null;
  const candidates = [
    j?.url, j?.link, j?.deletion_url && undefined,
    (j?.data as Record<string, unknown> | undefined)?.url,
  ];
  for (const c of candidates) if (typeof c === 'string' && c.startsWith('http')) return c;
  const m = raw.match(/https?:\/\/[^\s"']+/);
  if (m) return m[0];
  throw new Error('Upload succeeded but no URL in response');
}

/** Images → shx.gg (ShareX/sxcu-style). Token optional; 401/403 means anon is off. */
export async function uploadImage(blob: Blob, filename: string, token?: string): Promise<string> {
  const fd = new FormData();
  fd.append('file', blob, filename);
  const headers: Record<string, string> = {};
  if (token) headers['Authorization'] = token;
  const res = await fetch(SHX_URL, { method: 'POST', body: fd, headers });
  if (res.status === 401 || res.status === 403) {
    throw new Error('shx.gg rejected the upload (403). Anonymous uploads may be off — add your shx API token and retry.');
  }
  if (!res.ok) throw new Error(`shx.gg upload failed: HTTP ${res.status}`);
  const raw = await res.text();
  let json: unknown = null;
  try { json = JSON.parse(raw); } catch { /* plain-text URL response */ }
  return extractUrl(json, raw);
}

/** Files → userdrive (XFileSharing anon). Returns the /d/<code> download page link. */
export async function uploadFile(file: File | Blob, filename: string): Promise<string> {
  const fd = new FormData();
  fd.append('upload_type', 'file');
  fd.append('utype', 'anon');
  fd.append('file_0', file, filename);
  const res = await fetch(USERDRIVE_URL, { method: 'POST', body: fd });
  if (!res.ok) throw new Error(`userdrive upload failed: HTTP ${res.status}`);
  const json = await res.json() as Array<{ file_code?: string; file_status?: string }>;
  const first = json?.[0];
  if (!first?.file_code || first.file_status !== 'OK') {
    throw new Error('userdrive rejected the upload');
  }
  return USERDRIVE_LINK(first.file_code);
}
